/**
 * Explorer Handlers
 */

import { useCallback, type MouseEvent } from "react"
import { useNavigate } from "react-router-dom"
import { markTabClickNavigation, markProgrammaticNavigation } from "@/hooks/use-url-sync"
import type { OpenTab } from "../../features/document-tabs"
import { generateExplorerTabId, generateTabSlotId } from "../resizable-layout.utils"
import { pinHomeTabFirstInOpenTabs } from "./shared"
import { openSidebarMode } from "@/lib/sidebar-mode"
import type { usePerTabNavigationHistory } from "@/hooks/use-navigation-history"
import {
  buildAppUrl,
  documentUrlForPathOrId,
  normalizeFolderStoragePath,
  type WorkspaceRouteInfo,
} from "@/lib/app-routes"
import { resolveOpenIntent, type OpenIntentClickEvent } from "@/lib/open-intent"
import { navigationFieldsFromOpenTab } from "@/lib/basecamp-integration-nav"
import { recordNextcloudNavigation } from "@/lib/nextcloud-integration-nav"
import { getThread } from "@/lib/api/threads"
import { useWorkspace } from "@/providers/workspace-provider"
import { bindDriveIndexTabToWorkspace } from "@/components/integrations/google-workspace/drive-focus-workspace"
import type { NextcloudOpenDetail } from "@/components/integrations/nextcloud/nextcloud-browse-events"
import {
  FLOW_STUDIO_SURFACE_LABEL,
  flowStudioTabId,
  type FlowStudioSurface,
} from "@/components/features/flows/flow-studio-events"

export function useExplorerHandlers(
  openTabs: OpenTab[],
  setOpenTabs: (tabs: OpenTab[] | ((prev: OpenTab[]) => OpenTab[])) => void,
  activeTabId: string | null,
  setActiveTabId: (id: string | null) => void,
  setSelectedProjectId: (id: string | null) => void,
  setShowDocumentList: (show: boolean) => void,
  navigation: ReturnType<typeof usePerTabNavigationHistory>,
  navigate: ReturnType<typeof useNavigate>,
  getWorkspaceIdForPath?: (path: string) => string | undefined,
  workspacesRoute: WorkspaceRouteInfo[] = []
) {
  const { workspaces, activeWorkspaceIds } = useWorkspace()
  const driveWorkspaceId =
    activeWorkspaceIds
      .map((id) => workspaces.find((w) => w.id === id))
      .find((w) => w && w.type === 'work')?.id ?? activeWorkspaceIds[0]

  const resolveExplorerWs = (folderPath: string | null | undefined): string | undefined => {
    if (!folderPath || !getWorkspaceIdForPath) return undefined
    return getWorkspaceIdForPath(folderPath)
  }

  /**
   * Handle project/folder click
   */
  const handleProjectClick = useCallback((
    projectId: string,
    /** `true` = skip tab management; `MouseEvent` = sidebar folder click (Cmd/Ctrl opens new tab). */
    skipTabManagementOrEvent: boolean | MouseEvent = false,
    /** When skipTabManagement is true, caller may pass the slot that already received navigation (e.g. breadcrumb new-tab before activeTabId flushes). */
    historySlotId?: string
  ) => {
    const folderPath =
      projectId === ''
        ? null
        : normalizeFolderStoragePath(projectId, workspacesRoute) ?? projectId
    let skipTabManagement = false
    let modifierEvent: MouseEvent | undefined
    if (typeof skipTabManagementOrEvent === 'boolean') {
      skipTabManagement = skipTabManagementOrEvent
    } else if (skipTabManagementOrEvent) {
      modifierEvent = skipTabManagementOrEvent
    }

    const openInNewTab = modifierEvent ? (modifierEvent.metaKey || modifierEvent.ctrlKey) : false
    if (openInNewTab && !skipTabManagement) {
      const wsId = resolveExplorerWs(folderPath)
      const newTabSlotId = generateTabSlotId()
      const explorerTabId = generateExplorerTabId()
      const newTab: OpenTab = {
        id: explorerTabId,
        title: folderPath
          ? folderPath.split('/').filter(Boolean).pop() || 'Explorer'
          : 'Explorer',
        type: 'explorer',
        isDirty: false,
        isPinned: false,
        tabSlotId: newTabSlotId,
        explorerData: { folderPath },
        ...(wsId != null && { workspaceId: wsId }),
      }
      markProgrammaticNavigation()
      const targetUrl = folderPath ? documentUrlForPathOrId(folderPath, workspacesRoute) : '/'
      navigate(targetUrl)
      setSelectedProjectId(folderPath)
      setOpenTabs([...openTabs, newTab])
      setActiveTabId(explorerTabId)
      setShowDocumentList(true)
      navigation.navigate(
        newTabSlotId,
        { documentId: null, folderPath: folderPath !== undefined ? folderPath : null, viewMode: 'list' },
        'sidebar-folder-cmd-new-tab',
      )
      return
    }

    const currentTab = openTabs.find(tab => tab.id === activeTabId);

    markProgrammaticNavigation();

    const targetUrl = folderPath ? documentUrlForPathOrId(folderPath, workspacesRoute) : '/';
    navigate(targetUrl);
    // Note: do NOT call clearTabClickNavigation() here — it would immediately clear
    // the 'programmatic' flag set by markProgrammaticNavigation(), causing the URL
    // sync effect to fire with a stale location value and re-navigate to the old folder.
    // The flag is cleared automatically by markProgrammaticNavigation()'s 3s timeout.

    setSelectedProjectId(folderPath);

    const isFromHomeTab = currentTab?.type === 'home' || activeTabId === 'home'

    if (!skipTabManagement && isFromHomeTab) {
      const wsId = resolveExplorerWs(folderPath)
      const existingExplorer = openTabs.find((t) => t.type === 'explorer' || t.id === 'list')
      let explorerNavSlotId = 'tab-main'

      if (existingExplorer) {
        const updatedTabs = openTabs.map((tab) =>
          tab.id === existingExplorer.id
            ? {
                ...tab,
                explorerData: { folderPath },
                ...(wsId != null && { workspaceId: wsId }),
              }
            : tab,
        )
        setOpenTabs(updatedTabs)
        setActiveTabId(existingExplorer.id)
        explorerNavSlotId = existingExplorer.tabSlotId || 'tab-main'
      } else {
        const newTabSlotId = generateTabSlotId()
        const newTab: OpenTab = {
          id: 'list',
          title: 'Explorer',
          type: 'explorer',
          isDirty: false,
          tabSlotId: newTabSlotId,
          explorerData: { folderPath },
          ...(wsId != null && { workspaceId: wsId }),
        }
        setOpenTabs([...openTabs, newTab])
        setActiveTabId('list')
        explorerNavSlotId = newTabSlotId
      }

      setShowDocumentList(true)
      openSidebarMode('documents-tree')
      if (folderPath) {
        window.dispatchEvent(
          new CustomEvent('show-document-in-browse', {
            detail: { filePath: folderPath, forceRefresh: false },
          }),
        )
      }
      navigation.navigate(
        explorerNavSlotId,
        {
          documentId: null,
          folderPath: folderPath !== undefined ? folderPath : null,
          viewMode: 'list',
        },
        'browse-from-home',
      )
      return
    }

    if (skipTabManagement) {
      const wsId = resolveExplorerWs(folderPath)
      if (historySlotId) {
        setOpenTabs((prev) =>
          prev.map((tab) =>
            tab.tabSlotId === historySlotId && tab.type === "explorer"
              ? { ...tab, explorerData: { folderPath }, ...(wsId != null && { workspaceId: wsId }) }
              : tab
          )
        )
      } else if (currentTab?.type === "explorer") {
        const updatedTabs = openTabs.map((tab) =>
          tab.id === activeTabId
            ? { ...tab, explorerData: { folderPath }, ...(wsId != null && { workspaceId: wsId }) }
            : tab
        )
        setOpenTabs(updatedTabs)
      }
      setShowDocumentList(true);
      /** Pinned / breadcrumb-skip: stack key is explicit slot, else active tab row. */
      const explorerSlotForSkip = historySlotId || currentTab?.tabSlotId || "tab-main"
      const currentEntryMain = navigation.getCurrentEntry(explorerSlotForSkip);
      const currentFolderPath = currentEntryMain?.folderPath !== undefined ? currentEntryMain.folderPath : null;
      if (currentFolderPath !== folderPath) {
        navigation.navigate(explorerSlotForSkip, {
          documentId: null,
          folderPath: folderPath !== undefined ? folderPath : null,
          viewMode: 'list'
        }, "project-click-folder-changed-pinned");
      }
      return;
    }

    const wsId = resolveExplorerWs(folderPath)
    const explorerTab = openTabs.find(tab => tab.id === 'list' || tab.type === 'explorer');
    const explorerTabId = explorerTab?.id || 'list';

    /** Slot key for per-tab history: must match the explorer row we are driving. */
    let explorerNavSlotId = 'tab-main'

    if (currentTab?.type === 'explorer') {
      // Already on an explorer tab — just update the folder path in place
      const updatedTabs = openTabs.map(tab =>
        tab.id === activeTabId
          ? { ...tab, explorerData: { folderPath }, ...(wsId != null && { workspaceId: wsId }) }
          : tab
      );
      setOpenTabs(updatedTabs);
      explorerNavSlotId = currentTab.tabSlotId || 'tab-main'
    } else if (explorerTab) {
      // Explorer tab exists elsewhere — switch to it and update path.
      // Match by the specific tab's own id (not by type), otherwise this would
      // overwrite explorerData on EVERY open Explorer tab (e.g. one per workspace
      // group), causing folder content to bleed across unrelated tabs.
      const updatedTabs = openTabs.map(tab =>
        tab.id === explorerTab.id
          ? { ...tab, explorerData: { folderPath }, ...(wsId != null && { workspaceId: wsId }) }
          : tab
      );
      setOpenTabs(updatedTabs);
      setActiveTabId(explorerTabId);
      explorerNavSlotId = explorerTab.tabSlotId || 'tab-main'
    } else if (currentTab && !currentTab.isPinned) {
      // Unpinned tab (document, workspace-home, etc.) — replace in place with explorer
      const updatedTabs = openTabs.map(tab =>
        tab.id === activeTabId
          ? { id: 'list', title: 'Explorer', type: 'explorer' as const, isDirty: false,
              tabSlotId: tab.tabSlotId, explorerData: { folderPath },
              ...(wsId != null && { workspaceId: wsId }) }
          : tab
      );
      setOpenTabs(updatedTabs);
      setActiveTabId('list');
      explorerNavSlotId = currentTab.tabSlotId || 'tab-main'
    } else {
      // Current tab is pinned (or no active tab) — open new explorer tab
      const newTab: OpenTab = {
        id: "list",
        title: "Explorer",
        type: "explorer",
        isDirty: false,
        tabSlotId: "tab-main",
        explorerData: { folderPath },
        ...(wsId != null && { workspaceId: wsId }),
      };
      setOpenTabs([newTab, ...openTabs]);
      setActiveTabId("list");
      explorerNavSlotId = 'tab-main'
    }

    setShowDocumentList(true);

    const currentEntryMain = navigation.getCurrentEntry(explorerNavSlotId);
    const currentFolderPath = currentEntryMain?.folderPath !== undefined ? currentEntryMain.folderPath : null;

    if (currentFolderPath !== folderPath) {
      navigation.navigate(explorerNavSlotId, {
        documentId: null,
        folderPath: folderPath !== undefined ? folderPath : null,
        viewMode: 'list'
      }, "project-click-folder-changed");
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setSelectedProjectId, setShowDocumentList, navigation, navigate, workspacesRoute]);

  /**
   * Handle home click
   */
  const handleHomeClick = useCallback((event?: React.MouseEvent) => {
    const openInNewTab = event?.metaKey || event?.ctrlKey;
    const homeTabId = 'home';
    const existingHomeTab = openTabs.find(tab => tab.id === homeTabId);
    setSelectedProjectId(null)

    if (openInNewTab) {
      const newTabSlotId = generateTabSlotId();
      const newHomeTab: OpenTab = {
        id: `home-${Date.now()}`,
        title: 'Home',
        type: 'home' as const,
        isDirty: false,
        isPinned: false,
        tabSlotId: newTabSlotId,
      };
      setOpenTabs([...openTabs, newHomeTab]);
      setActiveTabId(newHomeTab.id);
      markTabClickNavigation();
      navigation.navigate(newTabSlotId, { documentId: null, viewMode: 'list' }, "home-click-cmd-new-tab");
      navigate('/', { replace: true });
    } else {
      if (existingHomeTab) {
        setOpenTabs(pinHomeTabFirstInOpenTabs(openTabs));
        setActiveTabId(homeTabId);
        markTabClickNavigation();
        navigate('/', { replace: true });
        const currentTab = openTabs.find(tab => tab.id === activeTabId);
        const tabSlotId = currentTab?.tabSlotId || 'tab-main';
        navigation.navigate(tabSlotId, { documentId: null, viewMode: 'list' }, "home-click-existing");
      } else {
        const currentTab = openTabs.find(tab => tab.id === activeTabId);
        if (currentTab?.isPinned === true) {
          const newTabSlotId = generateTabSlotId();
          const newHomeTab: OpenTab = {
            id: homeTabId,
            title: 'Home',
            type: 'home' as const,
            isDirty: false,
            isPinned: false,
            tabSlotId: newTabSlotId,
          };
          setOpenTabs([...openTabs, newHomeTab]);
          setActiveTabId(homeTabId);
          markTabClickNavigation();
          navigation.navigate(newTabSlotId, { documentId: null, viewMode: 'list' }, "home-click-pinned-new-tab");
          navigate('/', { replace: true });
        } else {
          const stableTabSlotId = currentTab?.tabSlotId || 'tab-main';
          const updatedTabs = openTabs.map(tab =>
            tab.id === activeTabId
              ? {
                  id: homeTabId,
                  title: 'Home',
                  type: 'home' as const,
                  isDirty: false,
                  tabSlotId: stableTabSlotId,
                }
              : tab
          );
          setOpenTabs(updatedTabs);
          setActiveTabId(homeTabId);
          markTabClickNavigation();
          navigation.navigate(stableTabSlotId, { documentId: null, viewMode: 'list' }, "home-click-replace-current");
          navigate('/', { replace: true });
        }
      }
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, setSelectedProjectId, navigate, navigation]);

  /**
   * Handle show file location
   */
  const handleShowFileLocation = useCallback((filePath: string) => {
    // Open the parent folder in the main panel
    const parts = filePath.split('/')
    const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : filePath
    handleProjectClick(folderPath);

    // Dispatch event so sidebar can expand + reveal the file
    window.dispatchEvent(new CustomEvent('show-document-in-browse', {
      detail: { filePath, documentId: filePath, forceRefresh: false }
    }))
  }, [handleProjectClick]);

  const handleInboxClick = useCallback(() => {
    const inboxTabId = 'inbox';
    const existingInboxTab = openTabs.find(tab => tab.id === inboxTabId);

    if (existingInboxTab) {
      setActiveTabId(inboxTabId);
      markTabClickNavigation();
    } else {
      const currentTab = openTabs.find(tab => tab.id === activeTabId);
      if (currentTab?.isPinned === true) {
        const newTabSlotId = generateTabSlotId();
        const newInboxTab: OpenTab = {
          id: inboxTabId,
          title: 'Inbox',
          type: 'inbox' as const,
          isDirty: false,
          isPinned: false,
          tabSlotId: newTabSlotId,
        };
        setOpenTabs([...openTabs, newInboxTab]);
        setActiveTabId(inboxTabId);
        markTabClickNavigation();
        navigation.navigate(newTabSlotId, { documentId: null, viewMode: 'list' }, "inbox-click-pinned-new-tab");
      } else {
        const stableTabSlotId = currentTab?.tabSlotId || 'tab-main';
        const updatedTabs = openTabs.map(tab =>
          tab.id === activeTabId
            ? {
                id: inboxTabId,
                title: 'Inbox',
                type: 'inbox' as const,
                isDirty: false,
                tabSlotId: stableTabSlotId,
              }
            : tab
        );
        setOpenTabs(updatedTabs);
        setActiveTabId(inboxTabId);
        markTabClickNavigation();
        navigation.navigate(stableTabSlotId, { documentId: null, viewMode: 'list' }, "inbox-click-replace-current");
      }
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute]);

  const handleDriveClick = useCallback(() => {
    const driveTabId = 'integration-google-drive'
    openSidebarMode('drive')
    const existingDriveTab = openTabs.find((tab) => tab.id === driveTabId || tab.type === 'integration-google-drive')
    const driveTab: OpenTab = {
      id: driveTabId,
      title: 'Google Drive',
      type: 'integration-google-drive',
      isDirty: false,
      isPinned: false,
      ...(driveWorkspaceId ? { workspaceId: driveWorkspaceId } : {}),
      integrationData: {
        integrationId: 'google-drive',
        ...(driveWorkspaceId ? { organizationId: driveWorkspaceId } : {}),
      },
    }

    if (existingDriveTab) {
      if (driveWorkspaceId && !existingDriveTab.workspaceId) {
        setOpenTabs((prev) => bindDriveIndexTabToWorkspace(prev, driveWorkspaceId))
      }
      setActiveTabId(existingDriveTab.id)
      markTabClickNavigation()
      navigate(buildAppUrl({ tab: { ...driveTab, tabSlotId: existingDriveTab.tabSlotId }, workspaces: workspacesRoute }), { replace: true })
      return
    }

    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (currentTab?.isPinned === true) {
      const newTabSlotId = generateTabSlotId()
      const newDriveTab: OpenTab = { ...driveTab, tabSlotId: newTabSlotId }
      setOpenTabs([...openTabs, newDriveTab])
      setActiveTabId(driveTabId)
      markTabClickNavigation()
      navigation.navigate(newTabSlotId, { documentId: null, viewMode: 'list' }, 'drive-click-pinned-new-tab')
      navigate(buildAppUrl({ tab: newDriveTab, workspaces: workspacesRoute }), { replace: true })
    } else {
      const stableTabSlotId = currentTab?.tabSlotId || 'tab-main'
      const updatedTabs = openTabs.map((tab) =>
        tab.id === activeTabId
          ? { ...driveTab, tabSlotId: stableTabSlotId }
          : tab,
      )
      setOpenTabs(updatedTabs)
      setActiveTabId(driveTabId)
      markTabClickNavigation()
      navigation.navigate(stableTabSlotId, { documentId: null, viewMode: 'list' }, 'drive-click-replace-current')
      navigate(buildAppUrl({ tab: { ...driveTab, tabSlotId: stableTabSlotId }, workspaces: workspacesRoute }), { replace: true })
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute, driveWorkspaceId])

  const handleNextcloudOpen = useCallback((detail: NextcloudOpenDetail) => {
    if (!detail?.organizationId || !detail.path || detail.selectOnly) return
    const tabId = 'integration-nextcloud'
    const nextcloudTab: OpenTab = {
      id: tabId,
      title: detail.name || 'Nextcloud',
      type: 'integration-nextcloud',
      isDirty: false,
      workspaceId: detail.organizationId,
      integrationData: {
        integrationId: 'nextcloud',
        organizationId: detail.organizationId,
        nextcloudPath: detail.path,
        nextcloudKind: detail.kind,
        ...(detail.fileId ? { nextcloudFileId: detail.fileId } : {}),
        ...(detail.previewPath ? { nextcloudPreviewPath: detail.previewPath } : {}),
        ...(detail.surface ? { nextcloudSurface: detail.surface } : {}),
      },
    }
    const existing = openTabs.find((tab) => tab.type === 'integration-nextcloud')
    if (existing) {
      const slot = existing.tabSlotId || 'tab-main'
      recordNextcloudNavigation(navigation, slot, existing, { ...nextcloudTab, tabSlotId: slot })
      setOpenTabs((prev) => prev.map((tab) => (
        tab.id === existing.id ? { ...nextcloudTab, tabSlotId: slot } : tab
      )))
      setActiveTabId(existing.id)
      markTabClickNavigation()
      navigate(buildAppUrl({ tab: { ...nextcloudTab, tabSlotId: slot }, workspaces: workspacesRoute }), { replace: true })
      return
    }

    const currentTab = openTabs.find((tab) => tab.id === activeTabId)
    if (currentTab?.isPinned === true) {
      const newTabSlotId = generateTabSlotId()
      const created: OpenTab = { ...nextcloudTab, tabSlotId: newTabSlotId }
      setOpenTabs([...openTabs, created])
      setActiveTabId(tabId)
      markTabClickNavigation()
      recordNextcloudNavigation(navigation, newTabSlotId, null, created)
      navigate(buildAppUrl({ tab: created, workspaces: workspacesRoute }), { replace: true })
      return
    }

    const stableTabSlotId = currentTab?.tabSlotId || 'tab-main'
    const replaced: OpenTab = { ...nextcloudTab, tabSlotId: stableTabSlotId }
    recordNextcloudNavigation(navigation, stableTabSlotId, currentTab ?? null, replaced)
    setOpenTabs(openTabs.map((tab) => (
      tab.id === activeTabId ? replaced : tab
    )))
    setActiveTabId(tabId)
    markTabClickNavigation()
    navigate(buildAppUrl({ tab: replaced, workspaces: workspacesRoute }), { replace: true })
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute])

  /**
   * Open a single inbox thread in the main panel (reader only). Used by sidebar inbox list.
   */
  const handleOpenInboxThreadMain = useCallback(
    async (
      threadId: string,
      title?: string,
      workspaceId?: string,
      clickEvent?: OpenIntentClickEvent,
    ) => {
      const tabId = `inbox-thread-${threadId}`;
      let rawTitle = (title?.trim() || "").slice(0, 80);

      let resolvedWorkspaceId = workspaceId;
      if (!rawTitle || !resolvedWorkspaceId) {
        try {
          const thread = await getThread(threadId);
          if (!rawTitle) rawTitle = (thread.title || "").trim().slice(0, 80);
          if (!resolvedWorkspaceId) {
            resolvedWorkspaceId =
              thread.organizationId ?? thread.workspaceId ?? undefined;
          }
        } catch {
          // Open without workspace; tab stays App-grouped until known
        }
      }
      if (!rawTitle) rawTitle = "Inbox thread";

      const activateInboxTab = (tab: OpenTab, reason: string) => {
        setActiveTabId(tab.id);
        markTabClickNavigation();
        markProgrammaticNavigation();
        navigate(buildAppUrl({ tab, workspaces: workspacesRoute }), { replace: true });
        navigation.navigate(
          tab.tabSlotId || "tab-main",
          navigationFieldsFromOpenTab(tab),
          reason,
        );
      };

      const existing = openTabs.find((tab) => tab.id === tabId);
      if (existing) {
        if (
          resolvedWorkspaceId &&
          existing.workspaceId !== resolvedWorkspaceId
        ) {
          const patched = { ...existing, workspaceId: resolvedWorkspaceId };
          setOpenTabs(
            openTabs.map((tab) => (tab.id === tabId ? patched : tab)),
          );
          activateInboxTab(patched, "inbox-thread-existing");
          return;
        }
        activateInboxTab(existing, "inbox-thread-existing");
        return;
      }

      const currentTab = openTabs.find((tab) => tab.id === activeTabId);
      const shouldAddNewTab =
        resolveOpenIntent(clickEvent, { currentTab, activeTabId }) === 'new';
      if (shouldAddNewTab) {
        const newTabSlotId = generateTabSlotId();
        const newTab: OpenTab = {
          id: tabId,
          title: rawTitle,
          type: "inbox-thread",
          isDirty: false,
          isPinned: false,
          tabSlotId: newTabSlotId,
          inboxThreadId: threadId,
          ...(resolvedWorkspaceId != null ? { workspaceId: resolvedWorkspaceId } : {}),
        };
        setOpenTabs([...openTabs, newTab]);
        activateInboxTab(newTab, "inbox-thread-sidebar-new-tab");
      } else {
        const stableTabSlotId = currentTab?.tabSlotId || "tab-main";
        const replacedTab: OpenTab = {
          id: tabId,
          title: rawTitle,
          type: "inbox-thread",
          isDirty: false,
          tabSlotId: stableTabSlotId,
          inboxThreadId: threadId,
          ...(resolvedWorkspaceId != null ? { workspaceId: resolvedWorkspaceId } : {}),
        };
        const updatedTabs = openTabs.map((tab) =>
          tab.id === activeTabId ? replacedTab : tab,
        );
        setOpenTabs(updatedTabs);
        activateInboxTab(replacedTab, "inbox-thread-sidebar-replace");
      }
    },
    [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute],
  );


  /** Open an agent chat thread as a chat-conversation editor tab. */
  const handleOpenChatConversationMain = useCallback(
    (
      threadId: string,
      title?: string,
      options?: { openInNewTab?: boolean; clickEvent?: OpenIntentClickEvent },
    ) => {
      const tabId = `chat-conversation-${threadId}`;
      const syncEditorBinding = () => {
        window.dispatchEvent(
          new CustomEvent('chat-editor-binding-sync', { detail: { threadId } }),
        )
      }
      const existing = openTabs.find((tab) => tab.id === tabId);
      if (existing) {
        if (title?.trim()) {
          const nextTitle = title.trim().slice(0, 80);
          setOpenTabs((prev) =>
            prev.map((tab) => (tab.id === tabId ? { ...tab, title: nextTitle } : tab)),
          );
        }
        setActiveTabId(tabId);
        markTabClickNavigation();
        markProgrammaticNavigation();
        navigate(
          buildAppUrl({
            tab: { ...existing, id: tabId, chatThreadId: threadId },
            workspaces: workspacesRoute,
          }),
          { replace: true },
        );
        syncEditorBinding()
        return;
      }
      const rawTitle = (title?.trim() || "New chat").slice(0, 80);
      const currentTab = openTabs.find((tab) => tab.id === activeTabId);
      const shouldAddNewTab =
        options?.openInNewTab === true ||
        resolveOpenIntent(options?.clickEvent, { currentTab, activeTabId }) === 'new';
      if (shouldAddNewTab) {
        const newTabSlotId = generateTabSlotId();
        const newTab: OpenTab = {
          id: tabId,
          title: rawTitle,
          type: "chat-conversation",
          isDirty: false,
          isPinned: false,
          tabSlotId: newTabSlotId,
          chatThreadId: threadId,
        };
        setOpenTabs([...openTabs, newTab]);
        setActiveTabId(tabId);
        markTabClickNavigation();
        markProgrammaticNavigation();
        navigate(buildAppUrl({ tab: newTab, workspaces: workspacesRoute }), { replace: true });
        navigation.navigate(newTabSlotId, { documentId: null, viewMode: "list" }, "chat-conversation-sidebar-new-tab");
        syncEditorBinding()
      } else {
        const stableTabSlotId = currentTab?.tabSlotId || "tab-main";
        const updatedTabs = openTabs.map((tab) =>
          tab.id === activeTabId
            ? {
                id: tabId,
                title: rawTitle,
                type: "chat-conversation" as const,
                isDirty: false,
                tabSlotId: stableTabSlotId,
                chatThreadId: threadId,
              }
            : tab,
        );
        setOpenTabs(updatedTabs);
        setActiveTabId(tabId);
        markTabClickNavigation();
        markProgrammaticNavigation();
        const replacedTab = updatedTabs.find((tab) => tab.id === tabId) ?? {
          id: tabId,
          title: rawTitle,
          type: "chat-conversation" as const,
          chatThreadId: threadId,
        };
        navigate(buildAppUrl({ tab: replacedTab as OpenTab, workspaces: workspacesRoute }), {
          replace: true,
        });
        navigation.navigate(stableTabSlotId, { documentId: null, viewMode: "list" }, "chat-conversation-sidebar-replace");
        syncEditorBinding()
      }
    },
    [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute],
  );

  /**
   * Open workspace home dashboard tab for a given workspace ID
   */
  const handleWorkspaceHomeClick = useCallback((workspaceId: string, workspaceName: string) => {
    const tabId = `workspace-home-${workspaceId}`
    const existing = openTabs.find(t => t.id === tabId)

    const activateWorkspaceHomeTab = (tab: OpenTab, historyReason: string) => {
      setActiveTabId(tab.id)
      markTabClickNavigation()
      markProgrammaticNavigation()
      navigate(buildAppUrl({ tab, workspaces: workspacesRoute }))
      navigation.navigate(tab.tabSlotId || 'tab-main', { documentId: null, viewMode: 'list' }, historyReason)
    }

    if (existing) {
      activateWorkspaceHomeTab(existing, "workspace-home-existing")
      return
    }

    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    const workspaceHomeTabFields = {
      id: tabId,
      title: workspaceName,
      type: 'workspace-home' as const,
      isDirty: false,
      isPinned: false,
      workspaceId,
    }

    const shouldAddNewTab = !currentTab || currentTab.isPinned === true

    if (shouldAddNewTab) {
      const newTabSlotId = generateTabSlotId()
      const newTab: OpenTab = { ...workspaceHomeTabFields, tabSlotId: newTabSlotId }
      setOpenTabs([...openTabs, newTab])
      activateWorkspaceHomeTab(newTab, "workspace-home-new-tab")
    } else {
      const stableTabSlotId = currentTab.tabSlotId || 'tab-main'
      const replacedTab: OpenTab = { ...workspaceHomeTabFields, tabSlotId: stableTabSlotId }
      const updatedTabs = openTabs.map(tab =>
        tab.id === activeTabId ? replacedTab : tab
      )
      setOpenTabs(updatedTabs)
      activateWorkspaceHomeTab(replacedTab, "workspace-home-replace-current")
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute])

  /**
   * Open Workspace Desk landing (My documents) for a workspace.
   */
  const handleDeskHomeClick = useCallback((
    workspaceId: string,
    workspaceName: string,
    deskPath: string,
    deskSlug: string,
  ) => {
    const tabId = `desk-home-${workspaceId}`
    const existing = openTabs.find(t => t.id === tabId)

    const activateDeskHomeTab = (tab: OpenTab, historyReason: string) => {
      setActiveTabId(tab.id)
      markTabClickNavigation()
      markProgrammaticNavigation()
      navigate(buildAppUrl({ tab, workspaces: workspacesRoute }))
      navigation.navigate(tab.tabSlotId || 'tab-main', { documentId: null, viewMode: 'list' }, historyReason)
    }

    if (existing) {
      activateDeskHomeTab(existing, "desk-home-existing")
      return
    }

    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    const deskHomeTabFields = {
      id: tabId,
      title: `My documents · ${workspaceName}`,
      type: 'desk-home' as const,
      isDirty: false,
      isPinned: false,
      workspaceId,
      filePath: deskPath || null,
      deskSlug,
    }

    const shouldAddNewTab = !currentTab || currentTab.isPinned === true

    if (shouldAddNewTab) {
      const newTabSlotId = generateTabSlotId()
      const newTab: OpenTab = { ...deskHomeTabFields, tabSlotId: newTabSlotId }
      setOpenTabs([...openTabs, newTab])
      activateDeskHomeTab(newTab, "desk-home-new-tab")
    } else {
      const stableTabSlotId = currentTab.tabSlotId || 'tab-main'
      const replacedTab: OpenTab = { ...deskHomeTabFields, tabSlotId: stableTabSlotId }
      const updatedTabs = openTabs.map(tab =>
        tab.id === activeTabId ? replacedTab : tab
      )
      setOpenTabs(updatedTabs)
      activateDeskHomeTab(replacedTab, "desk-home-replace-current")
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation, navigate, workspacesRoute])

  const handleFlowIndexClick = useCallback((surface: FlowStudioSurface) => {
    const tabId = flowStudioTabId(surface)
    const existing = openTabs.find(tab => tab.id === tabId)
    if (existing) {
      setActiveTabId(tabId)
      markTabClickNavigation()
      return
    }
    const fields: OpenTab = {
      id: tabId,
      title: FLOW_STUDIO_SURFACE_LABEL[surface],
      type: 'flow-index',
      isDirty: false,
      isPinned: false,
    }
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (!currentTab || currentTab.isPinned === true) {
      const newTabSlotId = generateTabSlotId()
      const newTab: OpenTab = { ...fields, tabSlotId: newTabSlotId }
      setOpenTabs([...openTabs, newTab])
      setActiveTabId(tabId)
      markTabClickNavigation()
      navigation.navigate(newTabSlotId, { documentId: null, viewMode: 'list' }, 'flow-index-new-tab')
    } else {
      const stableTabSlotId = currentTab.tabSlotId || 'tab-main'
      const replaced: OpenTab = { ...fields, tabSlotId: stableTabSlotId }
      setOpenTabs(openTabs.map(tab => tab.id === activeTabId ? replaced : tab))
      setActiveTabId(tabId)
      markTabClickNavigation()
      navigation.navigate(stableTabSlotId, { documentId: null, viewMode: 'list' }, 'flow-index-replace')
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId, markTabClickNavigation, navigation])

  return {
    handleProjectClick,
    handleHomeClick,
    handleInboxClick,
    handleDriveClick,
    handleNextcloudOpen,
    handleOpenInboxThreadMain,
    handleOpenChatConversationMain,
    handleShowFileLocation,
    handleWorkspaceHomeClick,
    handleDeskHomeClick,
    handleFlowIndexClick,
  };
}
